/**
 * The line icons, inline.
 *
 * Inline SVG and not an icon package: there are nine of them, they never
 * change, and a font or a sprite is a network round trip on the one screen
 * that must render before a customer has decided whether the kiosk is working.
 *
 * They are all `stroke="currentColor"` on a 24-box, so an icon takes the colour
 * of whatever plaque it is sitting in and nothing here needs to know about the
 * palette. The glyphs the screens used before this — `✕`, `−`, `⌕` — were
 * whatever the platform's UI face happened to draw, which on a Windows panel is
 * a different weight and a different optical size on every screen.
 */
export type IconName =
  | 'back'
  | 'forward'
  | 'cart'
  | 'close'
  | 'check'
  | 'card'
  | 'qr'
  | 'home'
  | 'search'
  | 'cup'
  | 'minus'
  | 'plus'
  | 'chevronUp'
  | 'chevronDown'
  | 'language'
  | 'bag'
  | 'receipt'
  | 'ticket'

const PATHS: Record<IconName, { d: string; fill?: boolean; width?: number }> = {
  back: { d: 'M10 19l-7-7m0 0l7-7m-7 7h18', width: 2.5 },
  forward: { d: 'M14 5l7 7m0 0l-7 7m7-7H3', width: 2.5 },
  cart: {
    d: 'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z'
  },
  close: { d: 'M6 18L18 6M6 6l12 12', width: 2.5 },
  check: { d: 'M5 13l4 4L19 7', width: 3 },
  card: { d: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z' },
  qr: {
    d: 'M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z'
  },
  home: {
    d: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6'
  },
  search: { d: 'M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z' },
  cup: { d: 'M18 8h1a4 4 0 010 8h-1M4 8h14v9a4 4 0 01-4 4H8a4 4 0 01-4-4V8zM6 2v2M10 2v2M14 2v2' },
  minus: { d: 'M5 12h14', width: 3 },
  plus: { d: 'M12 5v14M5 12h14', width: 3 },
  // The disclosure pair. A `plus` stood in for "show the breakdown" and read
  // as part of the total it sat beside — "9,500₮ +".
  chevronUp: { d: 'M5 15l7-7 7 7', width: 3 },
  chevronDown: { d: 'M19 9l-7 7-7-7', width: 3 },
  // A globe. It is the only glyph here that is not literally the thing it
  // names, and it is the one every kiosk in the world uses for this.
  language: {
    d: 'M12 3a9 9 0 100 18 9 9 0 000-18zm0 0c2.5 2.4 3.8 5.5 3.8 9s-1.3 6.6-3.8 9c-2.5-2.4-3.8-5.5-3.8-9s1.3-6.6 3.8-9zM3.4 9h17.2M3.4 15h17.2'
  },
  // Take-out. Dine-in is the cup already in this set.
  bag: { d: 'M8 8V6a4 4 0 018 0v2M6 8h12l1 13H5L6 8z' },
  // The two extras on the payment screen: a slip with a torn foot, and a
  // ticket with its notch — the company receipt and the coupon.
  receipt: { d: 'M6 3h12v18l-3-2-3 2-3-2-3 2V3zM9 8h6M9 12h6M9 16h3' },
  ticket: {
    d: 'M3 7a2 2 0 012-2h14a2 2 0 012 2v3a2 2 0 000 4v3a2 2 0 01-2 2H5a2 2 0 01-2-2v-3a2 2 0 000-4V7zM14 5v2m0 4v2m0 4v2'
  }
}

export function Icon({ name, className = 'size-8' }: { name: IconName; className?: string }) {
  const glyph = PATHS[name]
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={glyph.width ?? 2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={glyph.d} />
    </svg>
  )
}
